import cl from 'classnames';
import { throttle } from 'lodash';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { useDebounce, useEffectOnce } from 'react-use';
import { Editor } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import styled from 'styled-components';

import { colorsV2 } from '../../../../../colors-v2';
import { useDolEditorStore } from '../../../../dol-editor.store';
import { TableElement } from '../../../table/types';
import { TableBlogClassNames } from '../../const';
import { useAdminTableStore } from './table-adminview.store';
import { TableBaseProps } from './types';
import { useRootElement } from '../../../../../../slatePlugins/common/hocs/current-element.store';

const ELEMENT_H3 = 'h3';
const ELEMENT_ALIGN_LEFT = 'align_left';

const AdminTableCellExtra = dynamic<any>(
  import('./AdminTableCellExtra').then((mod) => mod.AdminTableCellExtra),
  { ssr: false }
);

interface MainTDProps {
  standardCellHeight?: number;
  bgColor?: string;
}

const TABLE_BORDER_COLOR = '#c1c7d0';

const MainTD = styled.td<MainTDProps>`
  position: relative;
  height: 49px;
  min-width: 80px;
  vertical-align: top;
  border-style: solid;
  border-width: 0 0 1px;
  border-color: ${TABLE_BORDER_COLOR};
  border-image: initial;
  padding: 18px 16px;

  h3 {
    color: unset;
    font-weight: unset;
    font-size: unset;
    margin: 0;
  }

  &.border {
    border-width: 1px 0 1px 1px;
    border-style: solid;
  }

  .table-resize-decoration {
    background-color: transparent;
    position: absolute;
    width: 10px;
    height: 100%;
    top: 0;
    right: -5px;
    cursor: col-resize;
    z-index: 109;

    &:after {
      content: '';
      right: 4px;
      position: absolute;
      width: 2px;
      height: calc(100% + 1px);
      background-color: transparent;
      z-index: 20;
      top: 0;
    }

    &.hovered {
      &:after {
        background-color: ${colorsV2.blue100};
      }
    }
  }

  .table-column-controls-decoration {
    cursor: pointer;
    position: absolute;
    width: calc(100% + 2px);
    left: -1px;
    top: -26px;
    height: 25px;
    box-sizing: border-box;
    display: block;
    margin-top: 0;

    &:after {
      content: '';
      background: ${colorsV2.gray10};
      border-top: 1px solid ${TABLE_BORDER_COLOR};
      border-right: 1px solid ${TABLE_BORDER_COLOR};
      border-left: 1px solid ${TABLE_BORDER_COLOR};
      border-image: initial;
      display: block;
      box-sizing: border-box;
      padding: 0;
      border-bottom: none;
      height: 11px;
      width: 100%;
      position: absolute;
      top: 14px;
      left: 0;
      z-index: 10;
    }
  }

  &.heading {
    background-color: ${(p) => p.bgColor || colorsV2.gray10};
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
  }

  &.last-row {
    border-bottom: 1px solid ${TABLE_BORDER_COLOR};
  }

  &.hover-on-add-button {
    .table-column-controls-decoration {
      &:after {
        z-index: 0;
      }
    }
  }

  &.hover-on-delete-table {
    border-color: ${colorsV2.red100};
    border-width: 1px;
    background: ${colorsV2.red10};

    &:after {
      background: ${colorsV2.red100};
    }

    .table-column-controls-decoration {
      &:after {
        background-color: ${colorsV2.red20};
        border-color: ${colorsV2.red100};
      }
    }
  }

  &.cell-hovered,
  &.cell-selected {
    border-color: ${colorsV2.blue100};
    border-width: 1px;

    .table-column-controls-decoration {
      &:after {
        color: ${colorsV2.white100};
        background-color: ${colorsV2.blue100};
        border-color: ${colorsV2.blue100};
        z-index: 11;
      }
    }

    &:after {
      content: '';
      position: absolute;
      height: 1px;
      background: ${colorsV2.blue100};
      top: -1px;
      z-index: 1;
      left: 0;
      right: 0;
    }

    &.hover-on-delete {
      background-color: ${colorsV2.red10};
      border-color: ${colorsV2.red100};

      .table-column-controls-decoration {
        &:after {
          background-color: ${colorsV2.red20};
          border-color: ${colorsV2.red100};
        }
      }

      &:after {
        background-color: ${colorsV2.red100};
      }
    }
  }

  &.is-in-table {
    &.first-row {
      &.column-selected {
        .table-column-controls-decoration {
          &:after {
            color: ${colorsV2.white100};
            background-color: ${colorsV2.blue100};
            border-color: ${colorsV2.blue100};
            z-index: 11;
          }
        }
      }

      &:before {
        content: '';
        background-color: ${TABLE_BORDER_COLOR};
        position: absolute;
        height: 4px;
        width: 4px;
        border-radius: 50%;
        pointer-events: none;
        top: -24px;
        right: -2px;
      }
    }
  }

  &.is-standard-cell-enabled {
    &:not(.is-standard-cell) {
      &.is-standard-cell-collapsed {
        .cell-wrapper {
          height: ${(p) => p.standardCellHeight}px;
          overflow: hidden;
        }
      }
    }
  }

  .slate-align-center {
    text-align: center;
  }

  .slate-align-left {
    text-align: left;
  }

  .slate-align-right {
    text-align: right;
  }

  ${(p) => p.bgColor && `background-color: ${p.bgColor};`}
`;

interface AdminTableCellProps extends TableBaseProps {}

const useGetCellNodeInfo = ({ editor, element, rowIndex }) => {
  const dolEditorStore = useDolEditorStore();
  const tableStore = useAdminTableStore();
  const [nodeInfo, setNodeInfo] = React.useState({
    align: ELEMENT_ALIGN_LEFT,
    alignPath: [],
    isH3Enabled: false,
    isStandardCellEnabled: false,
    isStandardCell: false,
    rowCollapsed: false,

    cellHeight: 0,
    standardCellHeight: 0,
    standardCellId: null,
  });

  const updateCellInfo = React.useCallback(
    ({ path }) => {
      let foundNode = null;
      let foundRowNode = null;
      try {
        foundNode = Editor.node(editor, path);
        foundRowNode = Editor.parent(editor, path);
        // eslint-disable-next-line no-empty
      } catch (ex) {}
      if (foundNode) {
        const [node, nodePath] = foundNode;
        const alignNode = node?.children?.[0];
        const textNode = alignNode?.children?.[0];
        let isStandardCellEnabled = false;
        let isStandardCell = false;
        let rowCollapsed = false;
        let cellHeight = 0;
        let standardCellHeight = 0;
        let standardCellId = null;

        if (foundRowNode) {
          const [rowNode] = foundRowNode as any;
          isStandardCellEnabled = Boolean(rowNode?.standardCellId);
          standardCellId = rowNode?.standardCellId;
          isStandardCell = rowNode?.standardCellId === element.id;
          rowCollapsed = Boolean(rowNode?.isCollapsed);

          if (isStandardCellEnabled) {
            const standardCellElm = document.querySelector(
              `[data-element-id="${rowNode?.standardCellId}"] .cell-wrapper`
            );
            if (standardCellElm) {
              standardCellHeight = standardCellElm.clientHeight;
            }
          }
        }

        const currentCellElm = document.querySelector(
          `[data-element-id="${element.id}"] .cell-wrapper`
        );
        if (currentCellElm) {
          cellHeight = Math.max(currentCellElm.clientHeight, currentCellElm.scrollHeight);
        }

        setNodeInfo({
          align: alignNode.type,
          alignPath: [...nodePath, 0],
          isH3Enabled: textNode?.type === ELEMENT_H3,
          isStandardCellEnabled,
          isStandardCell,
          rowCollapsed,
          cellHeight,
          standardCellHeight,
          standardCellId,
        });
        tableStore?.updateRowHeight();
      }
    },
    [editor, element.id, tableStore]
  );

  const [, cancel] = useDebounce(
    () => {
      if (tableStore?.isInTable) {
        const path = ReactEditor.findPath(editor, element);
        if (editor.selection) {
          const [foundRowNode] = Editor.nodes(editor, {
            at: editor.selection,
            match: (n: any) => n?.type === TableElement.TR,
          });
          if (foundRowNode) {
            const [, rowPath] = foundRowNode;
            const rowPathIndex = rowPath.pop();
            if (rowPathIndex === rowIndex) {
              updateCellInfo({ path });
            }
          }
        }
      }
    },
    400,
    [dolEditorStore.afterSelectionId]
  );

  useEffectOnce(() => {
    // setTimeout here just to make this init code run on separate thread
    setTimeout(() => {
      const path = ReactEditor.findPath(editor, element);
      updateCellInfo({ path });
    }, 10);
    cancel();
  });

  const updateNode = React.useMemo(() => {
    return throttle(() => {
      const path = ReactEditor.findPath(editor, element);
      updateCellInfo({ path });
    }, 400);
  }, [editor, element, updateCellInfo]);

  return {
    nodeInfo,
    updateNode,
  };
};

export const AdminTableTD = observer((props: AdminTableCellProps) => {
  const { children, attributes, element, isReadOnly, isTableCompletion } = props;
  const editor = useSlateStatic();
  const path = ReactEditor.findPath(editor as any, element);
  const columnIndex = path[path.length - 1];
  const rowIndex = path[path.length - 2];
  const tableStore = useAdminTableStore();
  const isLastRow = tableStore?.rowCount - 1 === rowIndex;
  const isFirstRow = rowIndex === 0;
  const { nodeInfo } = useGetCellNodeInfo({ editor, element, rowIndex });

  React.useEffect(() => {
    const elm = document.querySelector(`[data-element-id='${element.id}'] .cell-wrapper`);
    if (elm) {
      elm.scrollTop = 0;
    }
  }, [element.id, nodeInfo.rowCollapsed]);

  if (element.hidden) return null;

  return (
    <MainTD
      {...attributes}
      data-element-id={element.id}
      rowSpan={element.rowSpan}
      colSpan={element.colSpan}
      className={cl('table-blog-td', element.id, TableBlogClassNames.TABLE, {
        'hover-on-delete': tableStore?.selection?.isHoverOnDelete,
        'hover-on-delete-table': tableStore?.selection?.isTableDeleteHovered,
        'hover-on-add-button': tableStore?.selection?.isHoverOnAddButton,
        'last-row': isLastRow,
        'first-row': isFirstRow,
        'is-in-table': !isReadOnly && tableStore?.isInTable,
        border: tableStore?.hasBorder,
        heading:
          (tableStore?.hasColumnHeading && columnIndex === 0) ||
          (tableStore?.hasRowHeading && rowIndex === 0),

        'is-standard-cell': nodeInfo?.isStandardCell,
        'is-standard-cell-enabled': nodeInfo?.isStandardCellEnabled,
        'is-standard-cell-collapsed': nodeInfo?.rowCollapsed,

        'cell-hovered': tableStore?.cellSelection?.isInRange({
          rowIndex,
          columnIndex,
          rangeBound: tableStore?.cellSelection?.hoverSelectionRangeBound,
        }),
        'cell-selected': tableStore?.cellSelection?.isInRange({
          rowIndex,
          columnIndex,
          rangeBound: tableStore?.cellSelection?.selectionRangeBound,
        }),
        'is-cell-drag': tableStore?.cellSelection?.isDrag,
      })}
      bgColor={element.backgroundColor as string}
      standardCellHeight={nodeInfo?.standardCellHeight}
      onMouseDown={(event) => {
        if (tableStore.cellSelection.isPressShiftKey) {
          tableStore.cellSelection.mergeSelection({ rowIndex, columnIndex, editor, element });
          return;
        }
        tableStore.cellSelection.onSelectStart({ rowIndex, columnIndex, event, element });
        tableStore.selection.unselectAll();
      }}
      onMouseEnter={() => {
        if (tableStore.cellSelection.isDrag) {
          tableStore.cellSelection.mergeSelection({ rowIndex, columnIndex, editor, element });
        }
      }}
      onMouseUp={tableStore?.cellSelection?.onSelectStop}
    >
      <div aria-hidden className="cell-wrapper">
        {children}
      </div>
      <AdminTableCellExtra
        element={element as any}
        isReadOnly={isReadOnly}
        isTableCompletion={isTableCompletion}
      />
    </MainTD>
  );
});

const useNodeInfoView = (tableElement, element) =>
  React.useMemo(() => {
    let columnNodeIndex = -1;
    const rowNodeIndex = tableElement?.children?.findIndex((row) =>
      row.children.some((cell, index) => {
        const isMatch = cell.id === element.id;
        if (isMatch) {
          columnNodeIndex = index;
        }
        return isMatch;
      })
    );
    // let isStandardCellEnabled = false;
    // let isStandardCell = false;
    // let rowCollapsed = false;
    // let cellHeight = 0;
    // let standardCellHeight = 0;
    // let standardCellId = null;

    // if (rowNodeIndex !== -1) {
    //   const rowNode = tableElement.children[rowNodeIndex];
    //   isStandardCellEnabled = Boolean(rowNode?.standardCellId);
    //   standardCellId = rowNode?.standardCellId;
    //   isStandardCell = rowNode?.standardCellId === element.id;
    //   rowCollapsed = Boolean(rowNode?.isCollapsed);
    //
    //   if (isStandardCellEnabled) {
    //     const standardCellElm = document.querySelector(
    //       `[data-element-id="${rowNode?.standardCellId}"] .cell-wrapper`
    //     );
    //     if (standardCellElm) {
    //       standardCellHeight = standardCellElm.clientHeight;
    //     }
    //   }
    // }

    // const currentCellElm = document.querySelector(
    //   `[data-element-id="${element.id}"] .cell-wrapper`
    // );
    // if (currentCellElm) {
    //   cellHeight = Math.max(currentCellElm.clientHeight, currentCellElm.scrollHeight);
    // }

    return {
      hasBorder: tableElement?.border,
      hasColumnHeading: tableElement?.hasColumnHeading,
      hasRowHeading: tableElement?.hasRowHeading,
      rowIndex: rowNodeIndex,
      columnIndex: columnNodeIndex,
      isFirstRow: rowNodeIndex === 0,
      isLastRow: rowNodeIndex === tableElement?.children?.length - 1,
      //
      // isStandardCellEnabled,
      // isStandardCell,
      // rowCollapsed,
      // cellHeight,
      // standardCellHeight,
      // standardCellId,
    };
  }, [tableElement, element]);

export const TableTDReadOnly = (props: AdminTableCellProps) => {
  const { children, attributes, element } = props;
  const tableElement = useRootElement();
  const {
    isLastRow,
    isFirstRow,
    hasBorder,
    hasRowHeading,
    hasColumnHeading,
    rowIndex,
    columnIndex,

    // Standard / Collapse row feature
    // isStandardCell,
    // isStandardCellEnabled,
    // rowCollapsed,
    // standardCellHeight
  } = useNodeInfoView(tableElement, element);
  if (element.hidden) return null;

  return (
    <MainTD
      {...attributes}
      data-element-id={element.id}
      rowSpan={element.rowSpan}
      colSpan={element.colSpan}
      className={cl('table-blog-td', 'cell-wrapper', element.id, TableBlogClassNames.TABLE, {
        'last-row': isLastRow,
        'first-row': isFirstRow,
        border: hasBorder,
        heading: (hasColumnHeading && columnIndex === 0) || (hasRowHeading && rowIndex === 0),
      })}
      bgColor={element.backgroundColor as string}
    >
      {children}
    </MainTD>
  );
};
